/**
 * TRACEABILITY TAB (project scope only)
 *
 * Answers "which Jira issues have test scenarios covering them?" and "what
 * fraction of our scenarios are linked to requirements at all?".
 *
 * Sections:
 *   1. Coverage summary — covered vs uncovered scenario counts + coverage %
 *      visualised with a simple horizontal progress bar.
 *   2. Per-Jira-issue table — issue key, Jira id, scenario count.
 */

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '@atlaskit/spinner'
import { token } from '@atlaskit/tokens'

import MetricCardGrid from '../cards/MetricCardGrid'
import {
  fetchTraceabilityAsync,
  selectTraceability,
  selectReportLoading,
  selectReportErrors
} from '../../../store/slices/reportsSlice'

const CoverageBar = ({ covered, total }) => {
  const safeTotal = total || 0
  const pct = safeTotal === 0 ? 0 : Math.min(100, Math.max(0, (covered / safeTotal) * 100))
  return (
    <div
      style={{
        width: '100%',
        height: '12px',
        backgroundColor: token('color.background.neutral', '#F4F5F7'),
        borderRadius: '6px',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          backgroundColor: token('color.background.success.bold', '#22A06B'),
          transition: 'width 0.3s ease'
        }}
      />
    </div>
  )
}

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 500,
  color: token('color.text.subtle', '#6B778C'),
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px'
}

const tdStyle = {
  padding: '10px 12px',
  color: token('color.text', '#172B4D'),
  fontSize: '13px',
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
}

const rowBg = (index) =>
  index % 2 === 0
    ? token('elevation.surface', '#FFFFFF')
    : token('color.background.neutral.subtle', '#FAFBFC')

const TraceabilityTab = ({ projectId }) => {
  const dispatch = useDispatch()
  const data = useSelector(selectTraceability)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  useEffect(() => {
    if (projectId) {
      dispatch(fetchTraceabilityAsync({ projectId }))
    }
  }, [dispatch, projectId])

  if (loading.traceability) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spinner size="large" />
      </div>
    )
  }

  if (errors.traceability) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: token('color.text.danger', '#DE350B') }}>
        <p>Failed to load traceability</p>
        <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>{errors.traceability}</p>
      </div>
    )
  }

  if (!data) return null

  const {
    totalScenarios = 0,
    coveredScenarios = 0,
    uncoveredScenarios = 0,
    linkedIssues = 0,
    coverageRate = 0,
    rows = []
  } = data

  const metrics = [
    { value: linkedIssues, label: 'Linked Jira Issues' },
    { value: coveredScenarios, label: 'Covered Scenarios' },
    { value: uncoveredScenarios, label: 'Uncovered Scenarios' },
    { value: `${Number(coverageRate).toFixed(1)}%`, label: 'Coverage Rate' }
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <section>
        <MetricCardGrid metrics={metrics} compact gap="12px" />
      </section>

      <section>
        <div style={{ marginBottom: '8px' }}>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: token('color.text', '#172B4D')
            }}
          >
            Requirement coverage
          </div>
          <div
            style={{
              fontSize: '12px',
              color: token('color.text.subtle', '#6B778C'),
              marginTop: '2px'
            }}
          >
            {coveredScenarios} of {totalScenarios} scenarios are linked to at least one Jira issue.
          </div>
        </div>
        <CoverageBar covered={coveredScenarios} total={totalScenarios} />
      </section>

      <section>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: token('color.text', '#172B4D'),
            marginBottom: '8px'
          }}
        >
          Jira issues linked to scenarios
        </div>
        {rows.length === 0 ? (
          <div
            style={{
              padding: '32px',
              textAlign: 'center',
              color: token('color.text.subtle', '#6B778C'),
              fontSize: '13px',
              border: `1px dashed ${token('color.border', '#DFE1E6')}`,
              borderRadius: '4px'
            }}
          >
            No scenarios are linked to Jira issues yet.
          </div>
        ) : (
          <div
            style={{
              border: `1px solid ${token('color.border', '#DFE1E6')}`,
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: token('color.background.neutral', '#F4F5F7') }}>
                  <th style={thStyle}>Issue</th>
                  <th style={thStyle}>Jira ID</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Covering scenarios</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.jiraIssueKey || idx} style={{ backgroundColor: rowBg(idx) }}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{r.jiraIssueKey}</td>
                    <td style={{ ...tdStyle, color: token('color.text.subtle', '#6B778C') }}>
                      {r.jiraIssueId}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                      {r.scenarioCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default TraceabilityTab
